import { createContext, type FC, type ReactNode } from "react";

export type TranslateFn = (key: string, params?: Record<string, unknown>) => string;

export interface TranslationContextType {
  translate: TranslateFn;
}

export const TranslationContext = createContext<TranslationContextType>({
  translate: () => "",
});

interface TranslationProviderProps {
  children: ReactNode;
  translations: Record<string, string>;
}

export const TranslationProvider: FC<TranslationProviderProps> = ({
  children,
  translations = {},
}) => {
  const translate: TranslateFn = (key, params) => {
    if (!key) return "";

    // Check key existence (not truthiness) so an intentional empty-string
    // translation is preserved as "" rather than routed to the missing-key
    // error path.
    if (Object.prototype.hasOwnProperty.call(translations, key)) {
      let translatedText = translations[key];
      if (params) {
        Object.entries(params).forEach(([paramKey, paramValue]) => {
          // Use literal replaceAll instead of RegExp so a paramKey
          // containing regex meta-characters (e.g. `param.name`) doesn't
          // accidentally form a pattern.
          const placeholder = `%{${paramKey}}`;
          translatedText = translatedText.split(placeholder).join(String(paramValue));
        });
      }
      return translatedText;
    } else {
      console.error(`[Translation] Missing translation for key: "${key}"`);
      return "";
    }
  };

  return (
    <TranslationContext.Provider value={{ translate }}>
      {children}
    </TranslationContext.Provider>
  );
};
